import React from 'react';
import { useLocale } from '../../contexts/LocaleContext';
import { AppUser, UserRole } from '../../contexts/AuthContext';
import './SharedComponents.css';

interface PendingUser extends AppUser {
  isPending?: boolean;
}

interface UserTableProps {
  users: PendingUser[];
  currentUserId?: string;
  showOrganization?: boolean;
  organizations?: { id: string; name: string }[];
  isAdmin?: boolean;
  onUpdateRole?: (userId: string, role: UserRole) => void;
  onDeleteUser?: (userId: string, isPending: boolean) => void;
  emptyMessage?: string;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  currentUserId,
  showOrganization = false,
  organizations = [],
  isAdmin = false,
  onUpdateRole,
  onDeleteUser,
  emptyMessage,
}) => {
  const { t } = useLocale();

  const getOrgName = (orgId?: string) => {
    if (!orgId) return '-';
    return organizations.find(o => o.id === orgId)?.name || '-';
  };

  return (
    <table className="user-table">
      <thead>
        <tr>
          <th>{t('admin.name')}</th>
          <th>{t('admin.email')}</th>
          <th>{t('admin.role')}</th>
          {showOrganization && <th>{t('admin.organization')}</th>}
          <th>{t('admin.actions')}</th>
        </tr>
      </thead>
      <tbody>
        {users.length === 0 ? (
          <tr>
            <td colSpan={showOrganization ? 5 : 4} className="empty-cell">
              {emptyMessage || t('admin.noUsersFound')}
            </td>
          </tr>
        ) : (
          users.map(user => (
            <tr
              key={user.id}
              className={`${user.id === currentUserId ? 'current-user' : ''} ${user.isPending ? 'pending-user' : ''}`}
            >
              <td>
                {user.name}
                {user.isPending && (
                  <span className="pending-badge"> ({t('admin.pending')})</span>
                )}
              </td>
              <td>{user.email}</td>
              <td>
                {user.isPending ? (
                  <span>{user.role}</span>
                ) : (
                  <select
                    className="role-select"
                    value={user.role}
                    onChange={(e) => onUpdateRole?.(user.id, e.target.value as UserRole)}
                    disabled={user.id === currentUserId}
                  >
                    <option value="viewer">{t('admin.roleViewer')}</option>
                    <option value="org_user">{t('admin.roleUser')}</option>
                    <option value="org_admin">{t('admin.roleOrgAdmin')}</option>
                    {isAdmin && <option value="admin">{t('admin.rolePlatformAdmin')}</option>}
                  </select>
                )}
              </td>
              {showOrganization && (
                <td>{getOrgName(user.organizationId || undefined)}</td>
              )}
              <td>
                {user.id !== currentUserId && onDeleteUser && (
                  <button
                    className="btn-danger-small"
                    onClick={() => onDeleteUser(user.id, !!user.isPending)}
                  >
                    {t('general.delete')}
                  </button>
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

export default UserTable;
